/**
 * Better Auth Context for React
 * Provides authentication state and methods throughout the app
 */

import type React from "react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authClient, useSession } from "@/lib/auth-client";
import type { User } from "@/types/user";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  userTier: "free_user" | "standard_user" | "premium_user" | "free_provider" | "premium_provider" | "admin" | null;
  login: (email: string, password: string) => Promise<{ error?: string }>;
  signup: (
    email: string,
    password: string,
    name: string,
    userType?: string,
    metadata?: { postcode?: string; plan?: string }
  ) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  refreshUser: () => Promise<void>;
}

// Default SSR-safe values for when context is not available (e.g., during SSR)
// isLoading: true matches client initial state to prevent hydration mismatch
const defaultAuthContext: AuthContextType = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  userTier: null,
  login: async () => ({ error: "Auth not available" }),
  signup: async () => ({ error: "Auth not available" }),
  logout: async () => {},
  updateUser: () => {},
  refreshUser: async () => {},
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  // Return default SSR-safe values if no context (allows SSR to render headers)
  if (!context) {
    return defaultAuthContext;
  }
  return context;
};

// SSR_AUTH_PROVIDER: Lightweight provider for SSR that provides static defaults
// Used in entry-server.tsx to match client tree structure without client-only hooks
export const SSRAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <AuthContext.Provider value={defaultAuthContext}>{children}</AuthContext.Provider>;
};

interface AuthProviderProps {
  children: ReactNode;
}

// AUTH_CONTEXT: Better Auth provider component -> Related: auth-client.ts, auth.ts
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { data: session, isPending } = useSession();
  const [user, setUser] = useState<User | null>(null);
  const [userTier, setUserTier] = useState<AuthContextType["userTier"]>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Sync session with local user state
  useEffect(() => {
    if (!isPending) {
      if (session?.user) {
        // Map Better Auth session user to app User type
        const appUser: User = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.name,
          role: (session.user as any).role || "user",
          userTier: (session.user as any).userTier || "free_user",
          userType: (session.user as any).userType || "user",
          status: (session.user as any).status || "active",
          created_at: session.user.createdAt
            ? new Date(session.user.createdAt).getTime()
            : Date.now(),
          updated_at: session.user.updatedAt
            ? new Date(session.user.updatedAt).getTime()
            : Date.now(),
          createdAt: session.user.createdAt?.toString() || new Date().toISOString(),
          updatedAt: session.user.updatedAt?.toString() || new Date().toISOString(),
        };
        setUser(appUser);
        setUserTier((session.user as any).userTier || "free_user");
      } else {
        setUser(null);
        setUserTier(null);
      }
      setIsLoading(false);
    }
  }, [session, isPending]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const result = await authClient.signIn.email({
          email,
          password,
        });

        if (result.error) {
          return { error: result.error.message || "Login failed" };
        }

        // Navigate to dashboard after successful login
        navigate("/dashboard");
        return {};
      } catch (error) {
        return { error: error instanceof Error ? error.message : "Login failed" };
      }
    },
    [navigate]
  );

  const signup = useCallback(
    async (email: string, password: string, name: string, _userType?: string, _metadata?: { postcode?: string; plan?: string }) => {
      try {
        const result = await authClient.signUp.email({
          email,
          password,
          name,
        });

        if (result.error) {
          return { error: result.error.message || "Signup failed" };
        }

        // Navigate to dashboard after successful signup
        navigate("/dashboard");
        return {};
      } catch (error) {
        return { error: error instanceof Error ? error.message : "Signup failed" };
      }
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    try {
      await authClient.signOut();
      setUser(null);
      setUserTier(null);
      navigate("/");
    } catch (_error) {
      // Still clear local state even if signout fails
      setUser(null);
      setUserTier(null);
      navigate("/");
    }
  }, [navigate]);

  const updateUser = useCallback(
    (updates: Partial<User>) => {
      if (user) {
        setUser({ ...user, ...updates });
      }
    },
    [user]
  );

  const refreshUser = useCallback(async () => {
    // Better Auth automatically refreshes session
    // This is a no-op but kept for API compatibility
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading: isPending || isLoading,
        isAuthenticated: !!user,
        userTier,
        login,
        signup,
        logout,
        updateUser,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
